'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useAdminSessionStats } from '../_lib/hooks';
import { fmtSec, fmtTokens } from '../_lib/format';

const PERIODS = ['last7d', 'last30d', 'last60d', 'last90d', 'month', 'year'] as const;

export default function AdminSessionsListPage() {
  const sp = useSearchParams();
  const userIdQ = sp.get('userId') ?? '';
  const projectQ = sp.get('project') ?? '';
  const [userId, setUserId] = useState(userIdQ);
  const [projectBasename, setProjectBasename] = useState(projectQ);
  const [period, setPeriod] = useState<string>('last30d');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const { data, isFetching } = useAdminSessionStats({
    period,
    page,
    pageSize,
    userId: userId || undefined,
    projectBasename: projectBasename || undefined,
  });
  const loading = isFetching && !data;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Session 列表</h1>
        <Link href="/ai-usage" className="text-sm text-blue-600 hover:underline">
          ← 返回总览
        </Link>
      </div>

      <div className="bg-white rounded shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">期间</label>
          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              setPage(1);
            }}
            className="border rounded px-2 py-1 text-sm"
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">员工 ID 过滤</label>
          <input
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setPage(1);
            }}
            placeholder="UUID 完整匹配"
            className="border rounded px-2 py-1 text-sm w-72 font-mono"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">项目 basename 过滤</label>
          <input
            value={projectBasename}
            onChange={(e) => {
              setProjectBasename(e.target.value);
              setPage(1);
            }}
            placeholder="e.g. workspace"
            className="border rounded px-2 py-1 text-sm w-48"
          />
        </div>
        {(userId || projectBasename) && (
          <button
            className="text-xs text-gray-500 hover:text-gray-700"
            onClick={() => {
              setUserId('');
              setProjectBasename('');
              setPage(1);
            }}
          >
            清除过滤
          </button>
        )}
        <div className="ml-auto text-xs text-gray-500">
          {data && (
            <>
              共 <span className="font-semibold text-gray-800">{data.sessionCount}</span> 个 session ·
              平均 <span className="font-semibold text-gray-800">{(data.avgDurationSec / 60).toFixed(0)}min</span> ·
              平均 <span className="font-semibold text-gray-800">{data.avgTurns} turns</span>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-gray-500 border-b bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left">开始时间</th>
              <th className="py-2 px-3 text-left">员工</th>
              <th className="py-2 px-3 text-left">项目</th>
              <th className="py-2 px-3 text-left">模型</th>
              <th className="py-2 px-3 text-right">时长</th>
              <th className="py-2 px-3 text-right">Turns</th>
              <th className="py-2 px-3 text-right">Tokens</th>
              <th className="py-2 px-3 text-right">Cost</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data && data.recentSessions.length === 0 && (
              <tr>
                <td colSpan={9} className="py-8 text-center text-gray-400">
                  无数据
                </td>
              </tr>
            )}
            {!loading &&
              data?.recentSessions.map((s) => (
                <tr key={s.sessionId} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3 text-xs">{new Date(s.startedAt).toLocaleString()}</td>
                  <td className="py-2 px-3">{s.userLabel || s.userId?.slice(0, 8)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{s.projectBasename}</td>
                  <td className="py-2 px-3 font-mono text-xs">{s.model}</td>
                  <td className="py-2 px-3 text-right">{fmtSec(s.durationSec)}</td>
                  <td className="py-2 px-3 text-right">{s.turnCount}</td>
                  <td className="py-2 px-3 text-right">{fmtTokens(s.tokens)}</td>
                  <td className="py-2 px-3 text-right">${Number(s.costUsd).toFixed(3)}</td>
                  <td className="py-2 px-3 text-right">
                    <Link
                      href={`/ai-usage/sessions/${encodeURIComponent(s.sessionId)}`}
                      className="text-blue-600 hover:underline text-xs"
                    >
                      详情
                    </Link>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-gray-500">
            {data.pagination.page} / {data.pagination.totalPages}
          </span>
          <button
            disabled={page >= data.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}

